import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    SelectWrapper,
    SelectInputContainer,
    SelectInputLabel,
    StyledTrashIcon,
    ExpiryDisableContainer,
} from './PaymentMethodDropdown.style';
import { ChevronIconWrapper } from 'src/domains/layouts/webview/components/styles/baseDropdownStyle.style';
import { components, OptionProps, GroupBase } from 'react-select';
import { useAppStateContext } from 'src/appState/AppState';
import { getSavedCards } from 'src_server/trpc/types/payments';
import * as z from 'zod';
import { DateTime } from 'src_common/utils/time/time';
import { DeleteCardPopup } from 'src/domains/players/webview/components/WithdrawAndDeposit/components/DeleteCardPupup/DeleteCardPopup';
import { DeleteCardPopupState } from 'src/domains/players/webview/components/WithdrawAndDeposit/components/DeleteCardPupup/DeleteCardPopup.state';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';

const optionType = z.object({
    label: z.string(),
    value: z.string(),
    expires: z.string().optional().nullable(),
    isDisabled: z.boolean(),
    provider: z.string(),
});

export type OptionType = z.infer<typeof optionType>;

type CustomOptionPropsType<T = OptionType> = OptionProps<T, false, GroupBase<T>>;

export class PaymentMethodDropdownStateInner {
    public constructor(private readonly onChangeCB: (value: string) => void) {}

    public onChange = (value: string): void => {
        this.onChangeCB(value);
    };
}

export interface DropDownPropsType {
    allowAddingMethod?: boolean;
    label: string | JSX.Element;
    options: getSavedCards.SavedCardInterfaceType[] | null;
    value: string;
    onChangeCB: (value: string) => void;
    refreshPaymentMethods: () => Promise<void>;
}

const PaymentMethodOption = observer(
    'PaymentMethodOption',
    ({
        optionProps,
        handleOpenRemoveCardPopup,
    }: {
        optionProps: CustomOptionPropsType;
        handleOpenRemoveCardPopup: (id: string, label: string, provider: string) => void;
    }) => {
        const parsedData = optionType.safeParse(optionProps.data);

        if (parsedData.success === false) {
            console.error('Payment Methods Dropdown - payment method option error');
            return null;
        }

        const { isDisabled, expires, label, value, provider } = parsedData.data;

        return (
            <components.Option {...optionProps}>
                <div>{label}</div>
                {isDisabled ? (
                    <ExpiryDisableContainer>
                        Expired: {expires}
                        <StyledTrashIcon
                            onClick={(): void => {
                                handleOpenRemoveCardPopup(value, label.replaceAll(' ', '').slice(-8), provider);
                            }}
                        />
                    </ExpiryDisableContainer>
                ) : null}
            </components.Option>
        );
    }
);

export const PaymentMethodDropdown = observer('PaymentMethodDropdown', (props: DropDownPropsType) => {
    const { allowAddingMethod = false, label, options, value, onChangeCB, refreshPaymentMethods } = props;
    const { common } = useAppStateContext();

    const languageState = LanguagesState.get(common);
    const configState = ConfigComponents.get(common).config;

    const [state] = useState(() => new PaymentMethodDropdownStateInner(onChangeCB));
    const [deleteCardPopupState] = useState(() => new DeleteCardPopupState(common, refreshPaymentMethods));

    const onChange = (selectedOption: unknown): void => {
        const parsedOptionType = optionType.safeParse(selectedOption);
        if (parsedOptionType.success) {
            state.onChange(parsedOptionType.data.value);
            return;
        }
        console.error('Payment Methods Dropdown - payment method onChange error');
        return;
    };

    const allOptions = (): OptionType[] => {
        if (options === null) {
            return [];
        }

        const optionsParsed = options.map((v) => {
            const cardNumber = v.number === undefined ? '0000' : v.number;
            return {
                value: v.id,
                label: `**** **** **** ${cardNumber.slice(-4)}`,
                expires: DateTime.from(v.expires ?? undefined)?.format('MM/YY') ?? null,
                isDisabled: DateTime.current().isAfter(DateTime.from(v.expires ?? undefined) ?? DateTime.current()),
                provider: v.provider,
            };
        });

        if (allowAddingMethod) {
            optionsParsed.unshift({
                label: languageState.getTranslation('account.payment-method-dropdown.new.label', 'New payment method'),
                value: 'none',
                expires: null,
                isDisabled: false,
                provider: 'none',
            });
            return optionsParsed;
        }
        return optionsParsed;
    };

    const activeElement = (): OptionType | null => {
        const active = allOptions().find((option) => value === option.value) ?? null;
        return active;
    };

    const filteredOptions = (): OptionType[] => {
        return [...allOptions()].filter((option) => option.value !== activeElement()?.value);
    };

    return (
        <>
            <SelectInputContainer>
                <SelectInputLabel data-test='select-input-label'>{label}</SelectInputLabel>
                <SelectWrapper
                    value={activeElement()}
                    options={filteredOptions()}
                    className='custom-dropdown-select'
                    classNamePrefix='react-select'
                    onChange={onChange}
                    components={{
                        Option: (optionProps): JSX.Element | null => {
                            const parsedData = optionType.safeParse(optionProps.data);
                            if (!parsedData.success) return null;

                            const typedProps = Object.assign(
                                Object.create(Object.getPrototypeOf(optionProps)),
                                optionProps
                            );
                            typedProps.data = parsedData.data;

                            return (
                                <PaymentMethodOption
                                    optionProps={typedProps}
                                    handleOpenRemoveCardPopup={deleteCardPopupState.handleOpenRemoveCardPopup}
                                />
                            );
                        },
                        DropdownIndicator: (): JSX.Element => <ChevronIconWrapper position='down' />,
                    }}
                    isSearchable={false}
                    variant={configState.componentVariants.accountSectionVariant}
                />
            </SelectInputContainer>
            <DeleteCardPopup deleteCardPopupState={deleteCardPopupState} />
        </>
    );
});
